import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Box from 'Components/Atoms/Box';
import Container from 'Components/Atoms/Container';
import GridItem from 'Components/Atoms/GridItem';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { LanguageContext } from 'Contexts/LanguageContext';
import { AuthContext } from 'Contexts/AuthContext';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import SelectRestaurant from 'App/Dashboard/Components/SelectRestaurant';
import './styles.scss';

export type RestaurantDeletedScreenProps = {
  restaurantId: string;
  deletedAt: number | null;
};

const SUPPORT_EMAIL = 'support@gastronaut.ai';

const addMonths = (timestamp: number, months: number) => {
  const d = new Date(timestamp);
  d.setMonth(d.getMonth() + months);
  return d;
};

const RestaurantDeletedScreen: React.FC<RestaurantDeletedScreenProps> = ({
  restaurantId,
  deletedAt,
}) => {
  const { language, dayjs } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const { switchRestaurant } = useContext(RestaurantContext);
  const history = useHistory();
  const isDe = language === 'de';

  const userRestaurants = user?.data?.restaurants || [];

  const dataAvailableUntil = useMemo(() => {
    if (!deletedAt) return null;
    return addMonths(deletedAt, 6);
  }, [deletedAt]);

  const formattedDate = useMemo(() => {
    if (!dataAvailableUntil) return '';
    return dayjs(dataAvailableUntil).format(
      isDe ? 'D. MMMM YYYY' : 'MMMM D, YYYY',
    );
  }, [dataAvailableUntil, dayjs, isDe]);

  const subject = isDe
    ? `Datenanfrage gelöschtes Restaurant ${restaurantId}`
    : `Data request for deleted restaurant ${restaurantId}`;

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}`;

  const handleRestaurantChange = (rId: string) => {
    switchRestaurant(rId);
    history?.push(`/${rId}`);
  };

  const handleSettingsClick = (rId: string) => {
    switchRestaurant(rId);
    history?.push(`/${rId}/settings`);
  };

  return (
    <Container className="restaurant-deleted-screen-container">
      <GridItem xs={12}>
        <Box flex className="space-between" style={{ marginTop: 32 }}>
          <SelectRestaurant
            restaurantId={restaurantId}
            restaurants={userRestaurants}
            onRestaurantChange={handleRestaurantChange}
            onSettingsClick={handleSettingsClick}
          />
        </Box>
      </GridItem>

      <Box className="restaurant-deleted-screen">
        <Box className="restaurant-deleted-card">
          <Typography variant="h2" className="restaurant-deleted-title">
            {isDe ? 'Account deaktiviert' : 'Account deactivated'}
          </Typography>

          <Typography
            variant="text-2"
            component="p"
            className="restaurant-deleted-body"
          >
            {isDe
              ? 'Dieses Restaurant wurde deaktiviert. Es können keine Reservierungen mehr angenommen und keine Änderungen mehr vorgenommen werden. Sollte dies ein Fehler sein, rufe bitte 06221 / 357 0041 an!'
              : 'This restaurant has been deactivated. No new reservations can be accepted and no changes can be made. If this is a mistake, please call +49 6221 357 0041!'}
          </Typography>

          {dataAvailableUntil && (
            <Typography
              variant="text-2"
              component="p"
              className="restaurant-deleted-body"
            >
              {isDe ? (
                <>
                  Die Daten bleiben bis zum <strong>{formattedDate}</strong>{' '}
                  gespeichert. Bis dahin können Gäste, Reservierungen,
                  Gutscheine und Rechnungen per E-Mail beim Support angefordert
                  werden.
                </>
              ) : (
                <>
                  Your data is preserved until <strong>{formattedDate}</strong>.
                  Until then, guests, reservations, vouchers, and invoices can
                  be requested by emailing support.
                </>
              )}
            </Typography>
          )}

          <Box className="restaurant-deleted-actions">
            <a href={mailtoHref}>
              <Button variant="primary">
                {isDe ? 'Daten anfordern' : 'Request data'}
              </Button>
            </a>
          </Box>

          <Typography
            variant="text-4"
            component="p"
            className="restaurant-deleted-footnote"
          >
            {isDe ? 'Restaurant-ID: ' : 'Restaurant ID: '}
            <code>{restaurantId}</code>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default RestaurantDeletedScreen;
